import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { OrderRow, OrderStatus } from "@/pages/admin/orders/columns"; // Assuming OrderRow is defined here

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useTranslation } from "react-i18next";

interface ViewOrderDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  order: OrderRow | null;
}

const getOrderStatusVariant = (status: OrderStatus | undefined) => {
  if (!status) return "secondary";
  switch (status) {
    case "pending_payment":
    case "failed":
      return "outline";
    case "paid":
    case "processing":
      return "secondary";
    case "shipped":
    case "delivered":
    case "completed":
      return "default";
    case "cancelled":
    case "refunded":
      return "destructive";
    default:
      return "secondary";
  }
};

export default function ViewOrderDialog({ isOpen, onOpenChange, order }: ViewOrderDialogProps) {
  const { t } = useTranslation();

  if (!order) return null;

  // Helper to format currency
  const formatCurrency = (amount: string | number, currency: string) => {
    const numericAmount = typeof amount === 'string' ? Number.parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || "USD" }).format(numericAmount);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("admin.adminOrders.viewDialog.title", { orderNumber: order.orderNumber })}</DialogTitle>
          <DialogDescription>
            {t("admin.adminOrders.viewDialog.description", { orderDate: new Date(order.createdAt).toLocaleDateString() })}
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[60vh] p-1">
          <div className="space-y-4 pr-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold mb-1">{t("admin.adminOrders.viewDialog.customerDetails")}</h4>
                <p>{t("admin.adminOrders.viewDialog.email")}: {order.customerEmail}</p>
                {order.user?.email && order.user.email !== order.customerEmail && (
                  <p>{t("admin.adminOrders.viewDialog.accountEmail")}: {order.user.email}</p>
                )}
              </div>
              <div>
                <h4 className="font-semibold mb-1">{t("admin.adminOrders.viewDialog.orderDetails")}</h4>
                <p>{t("admin.adminOrders.viewDialog.status")}: <Badge variant={getOrderStatusVariant(order.orderStatus)}>{t(`enums.orderStatus.${order.orderStatus}`, order.orderStatus.replace(/_/g, " "))}</Badge></p>
                <p>{t("admin.adminOrders.viewDialog.paymentMethod")}: <Badge variant="outline">{t(`enums.paymentMethod.${order.paymentMethod}`, order.paymentMethod.replace(/_/g, " "))}</Badge></p>
                <p>{t("admin.adminOrders.viewDialog.totalAmount")}: {formatCurrency(order.totalAmount, order.currency)}</p>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-2">{t("admin.adminOrders.viewDialog.itemsOrdered")}</h4>
              {order.orderItems && order.orderItems.length > 0 ? (
                <ul className="space-y-2">
                  {order.orderItems.map((item, index) => (
                    <li key={`${item.product?.name || 'unknown-product'}-${index}`} className="p-2 border rounded-md">
                      <p className="font-medium">{item.product?.name || t("admin.adminOrders.unknownProduct")}</p>
                      <p>{t("admin.adminOrders.viewDialog.quantity")}: {item.quantity}</p>
                      {/* Add priceAtPurchase and productSnapshot details if available and needed */}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>{t("admin.adminOrders.viewDialog.noItems")}</p>
              )}
            </div>

            {/* Placeholder for payment snapshot, shipping/billing addresses if they exist */}
            {/* <pre className="text-xs bg-muted p-2 rounded-md overflow-x-auto">
              {JSON.stringify(order, null, 2)}
            </pre> */}
          </div>
        </ScrollArea>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("common.actions.close")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}